import { Component, Input, ViewChild, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter, Renderer2, ElementRef } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexPlotOptions,
  ApexChart,
  ApexFill,
  ApexXAxis,
  ApexDataLabels,
  ChartComponent,
  ApexStroke,
  ApexLegend,
  ApexGrid,
  ApexYAxis,
  ApexMarkers
} from "ng-apexcharts";
import { StylometricStateService } from '../../../services/stylometric-state.service';
import { Router } from '@angular/router';
import { Analysis, ANALYSIS_IDS } from '../../../models/stylometric.model';
import { Severity } from "../../shared/severity/severity.component";
import { ReportService } from '../../../services/report.service';
import { ReportComponent } from '../../../report.component';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  plotOptions: ApexPlotOptions;
  fill: ApexFill;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  grid: ApexGrid;
  yaxis: ApexYAxis;
  colors: string[];
  markers: ApexMarkers;
  xaxis: ApexXAxis;
};

// Add new interface for Project Synapse stylometric data before the @Component decorator
interface ProjectSynapseStylometrics {
  features: {
    lexical: {
      avg_word_length: number;
      vocabulary_richness: number;
      type_token_ratio: number;
      hapax_ratio: number;
      char_diversity: number;
      word_length_variance: number;
      unique_words_ratio: number;
      punctuation_ratio: number;
    };
    syntactic: {
      avg_sentence_length: number;
      sentence_complexity: number;
      avg_parse_tree_depth: number;
      parse_tree_breadth: number;
      syntactic_diversity: number;
      subordinate_clause_ratio: number;
      function_word_ratio: number;
    };
    structural: {
      paragraph_length_variance: number;
      paragraph_count: number;
      text_density: number;
      whitespace_ratio: number;
      line_break_frequency: number;
      avg_sentences_per_paragraph: number;
      structure_consistency: number;
    };
    readability: {
      flesch_reading_ease: number;
      flesch_kincaid_grade: number;
      gunning_fog: number;
      smog_index: number;
      automated_readability_index: number;
      average_syllables_per_word: number;
      complex_word_ratio: number;
    };
  };
}

@Component({
  selector: 'app-stylometrics',
  templateUrl: './stylometrics.component.html',
  styleUrls: ['./stylometrics.component.scss']
})

export class StylometricsComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild("chart") chart!: ChartComponent;
  @Input() comparisonResult: string = 'none';
  @Input() previousSubmissions: any;
  @Input() stylometricMetrics: any;
  @Input() metricsThresholds: any;
  @Input() averageStylisticDiscrepancy: string;

  public isCollapsed = false;
  public chartOptions: Partial<ChartOptions>;
  public activeAnalysis: string | null = null;
  isStylometricReportOpen = false;
  selectedAnalysis = '';
  private chartInitialized = false;
  private pendingChartUpdate: Partial<ChartOptions> | null = null;

  // Separate data structures for stylometrics and Project Synapse
  availableSubmissions: {
    value: string;
    label: string;
    title: string;
    fileUrl?: string;
    discrepancy?: string;
  }[] = [];

  availableAIModels: {
    value: string;
    label: string;
    title: string;
    fileUrl: string;
    comparisonData: any;
  }[] = [];

  currentSeverity: 'low' | 'medium' | 'high' = 'high';

  @Input() projectSynapse: boolean = true;
  @Input() projectSynapseData: any[] = [];
  currentProjectSynapseData: any;
  currentModelComparisonData: any = null;
  isProjectSynapseActive = false;
  currentDate = new Date();
  currentAIModel = '';
  mostProbableModel = '';

  @Input() analysisDate: any;

  private readonly LARGE_CHART_SIZE = 100;
  private readonly SMALL_CHART_SIZE = 90;
  private readonly SMALLER_CHART_SIZE = 70;
  private readonly SMALLER_CHART_SIZE_2 = 60;
  private readonly SMALLER_CHART_SIZE_3 = 50;

  public analyses: Analysis[] = [
    {
      id: ANALYSIS_IDS.LEXICAL,
      type: 'Lexical',
      name: 'Lexical Analysis',
      title: 'Lexical Analysis',
      flagCount: 0,
      description: 'Lexical analysis evaluates the choice and diversity of words used in the text.',
      metrics: []
    },
    {
      id: ANALYSIS_IDS.SYNTACTIC,
      type: 'Syntactic',
      name: 'Syntactic Analysis',
      title: 'Syntactic Analysis',
      flagCount: 0,
      description: 'Syntactic analysis looks at the structure and complexity of sentences.',
      metrics: []
    },
    {
      id: ANALYSIS_IDS.STRUCTURAL,
      type: 'Structural',
      name: 'Structural Analysis',
      title: 'Structural Analysis',
      flagCount: 0,
      description: 'Structural analysis evaluates the organization and layout of the text.',
      metrics: []
    },
    {
      id: ANALYSIS_IDS.READABILITY,
      type: 'Readability',
      name: 'Readability Analysis',
      title: 'Readability Analysis',
      flagCount: 0,
      description: 'Readability evaluates how easily the text can be understood.',
      metrics: []
    }
  ];

  @Output() discrepancyLevelChange = new EventEmitter<string>();

  public projectSynapseStylisticDiscrepancy: string = 'low';

  reportType: 'classic' | 'stylometric' = 'classic';

  constructor(
    private stylometricState: StylometricStateService,
    private router: Router,
    private reportService: ReportService,
    private cdr: ChangeDetectorRef,
    private reportComponent: ReportComponent,
    private renderer: Renderer2,
    private el: ElementRef
  ) {
    // Initialize chart options with default values and correct colors
    this.chartOptions = {
      series: [
        {
          name: "Current submission",
          data: [0, 0, 0, 0]
        },
        {
          name: "AVG. Previous submissions",
          data: [0, 0, 0, 0]
        }
      ],
      chart: {
        type: "radar",
        height: 280,
        toolbar: {
          show: false
        },
        fontFamily: 'Inter, sans-serif',
        animations: {
          enabled: false
        }
      },
      plotOptions: {
        radar: {
          size: this.LARGE_CHART_SIZE,
          offsetY: 0,
          polygons: {
            strokeWidth: '1',
            strokeColors: '#e8e8e8',
            connectorColors: '#e8e8e8',
            fill: {
              colors: ['#fcfcfc', '#ffffff']
            }
          }
        }
      },
      colors: ["#D42620", "#147AD6"],
      fill: {
        opacity: 0.15
      },
      stroke: {
        width: 2,
        colors: ["#D42620", "#147AD6"]
      },
      markers: {
        size: 4,
        hover: {
          size: 6
        }
      },
      xaxis: {
        categories: ["Lexical", "Syntactic", "Structural", "Readability"],
        labels: {
          style: {
            colors: Array(4).fill("#666666"),
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
          }
        }
      },
      yaxis: {
        show: false
      },
      grid: {
        show: true,
        padding: {
          top: 20,
          right: 20,
          bottom: 40,
          left: 20
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 0,
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        markers: {
          width: 12,
          height: 12,
          radius: 2
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0
        }
      }
    };
  }

  ngOnInit() {
    this.stylometricState.isProjectSynapseActive$.subscribe(isActive => {
      this.isProjectSynapseActive = isActive;

      // If Project Synapse is active, initialize its data structure
      if (isActive && this.projectSynapseData && this.projectSynapseData.length > 0) {
        this.initializeProjectSynapseData(true);
      } else if (!isActive && this.previousSubmissions) {
        // Initialize stylometric data when Project Synapse is not active
        this.initializeStylometricData();
      }
    });

    this.stylometricState.isStylometricReportOpen$.subscribe(isOpen => {
      this.isStylometricReportOpen = isOpen;
    });

    this.stylometricState.selectedAnalysis$.subscribe(analysis => {
      this.selectedAnalysis = analysis;
    });

    this.stylometricState.selectedAIModel$.subscribe(model => {
      if (model) {
        this.currentAIModel = model.value;

        // Update Project Synapse data when model changes
        if (this.isProjectSynapseActive && this.projectSynapseData) {
          const selectedModel = this.projectSynapseData.find(m => m.modelName === model.value);
          if (selectedModel && selectedModel.comparisonData && selectedModel.comparisonData.stylometrics) {
            // We'll extract data directly from comparison sections when needed
            // this.currentProjectSynapseData = selectedModel.comparisonData.stylometrics.features;
            // Set the current model's full comparison data
            this.currentModelComparisonData = selectedModel.comparisonData;

            // Extract stylistic discrepancy from the backend data
            let discrepancyFromBackend = null;

            // Check different possible locations for the stylistic_discrepancy
            if (selectedModel.comparisonData.stylometrics?.stylistic_discrepancy) {
              discrepancyFromBackend = selectedModel.comparisonData.stylometrics.stylistic_discrepancy;
            } else if (selectedModel.comparisonData.document_stylometrics?.stylistic_discrepancy) {
              discrepancyFromBackend = selectedModel.comparisonData.document_stylometrics.stylistic_discrepancy;
            } else {
              // Check in each section's stylometrics (take from first section that has it)
              const sections = selectedModel.comparisonData.comparison || {};
              for (const sectionKey in sections) {
                const section = sections[sectionKey];
                if (section?.stylometrics?.stylistic_discrepancy) {
                  discrepancyFromBackend = section.stylometrics.stylistic_discrepancy;
                  break; // Use the first one found
                }
              }
            }

            // Update stylistic discrepancy if available from backend
            if (discrepancyFromBackend) {
              this.projectSynapseStylisticDiscrepancy = discrepancyFromBackend.toLowerCase();
              // Emit discrepancy level change to parent component
              this.discrepancyLevelChange.emit(this.projectSynapseStylisticDiscrepancy);
            }

            this.updateAnalysisFlagCounts();
            this.cdr.detectChanges();
          }
        } else if (this.availableAIModels && this.availableAIModels.length > 0) {
          // If not using projectSynapseData, try to get from availableAIModels
          const modelFromAvailable = this.availableAIModels.find(m => m.value === model.value);
          if (modelFromAvailable && modelFromAvailable.comparisonData) {
            this.currentModelComparisonData = modelFromAvailable.comparisonData;
            if (modelFromAvailable.comparisonData.stylometrics) {
              // We'll extract data directly from comparison sections when needed
              // this.currentProjectSynapseData = modelFromAvailable.comparisonData.stylometrics.features;

              // Extract stylistic discrepancy from the backend data
              let discrepancyFromBackend = null;

              // Check different possible locations for the stylistic_discrepancy
              if (modelFromAvailable.comparisonData.stylometrics?.stylistic_discrepancy) {
                discrepancyFromBackend = modelFromAvailable.comparisonData.stylometrics.stylistic_discrepancy;
              } else if (modelFromAvailable.comparisonData.document_stylometrics?.stylistic_discrepancy) {
                discrepancyFromBackend = modelFromAvailable.comparisonData.document_stylometrics.stylistic_discrepancy;
              } else {
                // Check in each section's stylometrics (take from first section that has it)
                const sections = modelFromAvailable.comparisonData.comparison || {};
                for (const sectionKey in sections) {
                  const section = sections[sectionKey];
                  if (section?.stylometrics?.stylistic_discrepancy) {
                    discrepancyFromBackend = section.stylometrics.stylistic_discrepancy;
                    break; // Use the first one found
                  }
                }
              }

              // Update stylistic discrepancy if available from backend
              if (discrepancyFromBackend) {
                this.projectSynapseStylisticDiscrepancy = discrepancyFromBackend.toLowerCase();
                // Emit discrepancy level change to parent component
                this.discrepancyLevelChange.emit(this.projectSynapseStylisticDiscrepancy);
              }
            }
            this.cdr.detectChanges();
          }
        }
      }
    });

    // Subscribe to selected submission changes from the service
    this.stylometricState.selectedSubmission$.subscribe(submission => {
      // Only update if the value actually changed to prevent duplicate redraws
      if (submission && submission !== this.comparisonResult) {
        this.comparisonResult = submission;

        // Update the chart when the submission changes via the service
        if (!this.isProjectSynapseActive) {
          setTimeout(() => {
            this.initializeChartOptions();
            this.updateAnalysisFlagCounts();
            this.cdr.detectChanges();
          }, 100);
        }
      }
    });

    // Add subscription to reportService to detect when analysis type changes
    this.reportService.selectAnalysisTypeSubject.subscribe(analysisType => {
      // If the user navigates to metadata while Project Synapse is active,
      // deactivate Project Synapse
      if (analysisType === 'metadata' && this.isProjectSynapseActive) {
        // Deactivate Project Synapse
        this.stylometricState.toggleProjectSynapseActive(false);
        this.isProjectSynapseActive = false;

        // Initialize regular stylometric data
        if (this.previousSubmissions) {
          this.initializeStylometricData();
        }

        // Force change detection to update UI
        this.cdr.detectChanges();
      }
    });

    // Initialize chart when data is available
    if (this.stylometricMetrics && this.previousSubmissions && this.metricsThresholds) {
      this.initializeChartOptions();
    }

    // Initialize stylometric data if Project Synapse is not active and we have previous submissions
    if (!this.isProjectSynapseActive && this.previousSubmissions) {
      this.initializeStylometricData();
    }

    // Add resize listener
    window.addEventListener('resize', () => this.updateChartSize());
  }

  // New method to safely update chart options
  private safeUpdateChart(options: Partial<ChartOptions>, forceRender: boolean = false, animate: boolean = false): void {
    // Store the options for later if chart isn't ready
    this.chartOptions = options;

    // If chart isn't initialized yet, store the update for later
    if (!this.chart) {
      this.pendingChartUpdate = options;
      return;
    }

    // Mark chart as initialized
    this.chartInitialized = true;

    // Only try to update if updateOptions is a function
    if (typeof this.chart.updateOptions === 'function') {
      try {
        // Force render and animate to ensure the chart updates visually
        this.chart.updateOptions(options, true, true);
      } catch (error) {
        // Just store the options without updating the chart
        this.chartOptions = options;
      }
    }
  }

  // Add hook for when chart is available
  ngAfterViewInit() {
    // Apply any pending chart updates
    if (this.pendingChartUpdate && this.chart) {
      setTimeout(() => {
        this.safeUpdateChart(this.pendingChartUpdate!, true);
        this.pendingChartUpdate = null;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update severity when averageStylisticDiscrepancy changes
    if (changes.averageStylisticDiscrepancy?.currentValue) {
      this.currentSeverity = changes.averageStylisticDiscrepancy.currentValue.toLowerCase() as 'low' | 'medium' | 'high';
    }

    // Handle Project Synapse flag or data changes
    if (changes.projectSynapse && changes.projectSynapse.currentValue !== changes.projectSynapse.previousValue) {
      // If we're currently in stylometric view, return to classic view
      if (this.reportType === 'stylometric') {
        this.reportComponent.switchReportType('classic');
        this.reportType = 'classic';
        this.isStylometricReportOpen = false;
        this.selectedAnalysis = '';
        this.activeAnalysis = null;
      }

      // Only update the Project Synapse state if the parent component explicitly requested it
      // Don't activate it just because data is available
      if (changes.projectSynapse.currentValue && this.projectSynapseData?.length > 0) {
        this.initializeProjectSynapseData(false);
      } else if (this.previousSubmissions) {
        this.initializeStylometricData();
      }
    }

    // Handle Project Synapse data changes
    if (changes.projectSynapseData && this.projectSynapse &&
        changes.projectSynapseData.currentValue !== changes.projectSynapseData.previousValue) {
      // Only initialize the data without activating Project Synapse mode
      this.initializeProjectSynapseData(false);
    }

    // Update flag counts when relevant data changes
    if (changes.stylometricMetrics?.currentValue ||
        changes.previousSubmissions?.currentValue ||
        changes.metricsThresholds?.currentValue ||
        changes.comparisonResult?.currentValue) {
      this.updateAnalysisFlagCounts();
    }

    // Update chart when input data changes
    if ((changes.stylometricMetrics?.currentValue ||
         changes.previousSubmissions?.currentValue ||
         changes.metricsThresholds?.currentValue ||
         changes.comparisonResult?.currentValue) &&
        this.stylometricMetrics &&
        this.previousSubmissions &&
        this.metricsThresholds) {
      // Use setTimeout to ensure this runs after the view is initialized
      setTimeout(() => {
        this.initializeChartOptions();
      });
    }
  }

  private updateChartSize(): void {
    // Add a guard clause to prevent errors when chartOptions is undefined
    if (!this.chartOptions || !this.chartOptions.plotOptions || !this.chartOptions.plotOptions.radar) {
      return;
    }

    const chartSize = window.innerWidth <= 450
      ? this.SMALLER_CHART_SIZE_3
      : window.innerWidth <= 500
        ? this.SMALLER_CHART_SIZE_2
        : window.innerWidth <= 900
          ? this.LARGE_CHART_SIZE
          : window.innerWidth <= 1255
            ? this.SMALLER_CHART_SIZE
            : window.innerWidth <= 1380
              ? this.SMALL_CHART_SIZE
              : this.LARGE_CHART_SIZE;

    const fontSize = window.innerWidth <= 450 ? '8px' : '12px';

    if (this.chartOptions?.plotOptions?.radar) {
      this.chartOptions.plotOptions.radar.size = chartSize;

      // Update font size for labels
      if (this.chartOptions.xaxis?.labels?.style) {
        this.chartOptions.xaxis.labels.style.fontSize = fontSize;
      }

      // Update font size for legend
      if (this.chartOptions.legend) {
        this.chartOptions.legend.fontSize = fontSize;
      }

      // Use the safe update method
      this.safeUpdateChart(this.chartOptions);
    }
  }

  private initializeChartOptions(): void {
    // Add a guard clause to prevent errors when stylometricMetrics is undefined
    if (!this.stylometricMetrics) {
      return;
    }

    const initialSize = window.innerWidth <= 500
      ? this.SMALLER_CHART_SIZE_2
      : window.innerWidth <= 900
        ? this.LARGE_CHART_SIZE
        : window.innerWidth <= 1255
          ? this.SMALLER_CHART_SIZE
          : window.innerWidth <= 1380
            ? this.SMALL_CHART_SIZE
            : this.LARGE_CHART_SIZE;

    // Get sums for current submission
    const analysisTypes = ['lexical', 'syntactic', 'structural', 'readability'];
    const currentData = analysisTypes.map(analysisType => {
      const metrics = this.stylometricMetrics?.[analysisType];
      if (!metrics || !Array.isArray(metrics)) return 0;

      // Sum the 'value' property from each metric object and fix to 4 decimal places
      const sum = metrics.reduce((acc: number, metric: any) => {
        const numValue = Number(metric.value);
        return acc + (isNaN(numValue) ? 0 : numValue);
      }, 0);

      return Number(sum.toFixed(4));
    }) as number[];

    // Get sums for previous submissions with improved error handling
    const previousData = analysisTypes.map((analysisType, index) => {
      let metrics = null;

      // Make sure we have something to work with
      if (!this.previousSubmissions) {
        return currentData[index]; // fallback to current data
      }

      if (this.comparisonResult === 'all') {
        // Check if 'all' exists directly on previousSubmissions
        if (this.previousSubmissions.all && this.previousSubmissions.all[analysisType]) {
          metrics = this.previousSubmissions.all[analysisType];
        }
      } else if (this.comparisonResult === 'none' && this.previousSubmissions.all) {
        // If no specific comparison is selected, use 'all' as default
        metrics = this.previousSubmissions.all[analysisType];
      } else if (this.previousSubmissions.individual && Array.isArray(this.previousSubmissions.individual)) {
        const selectedSubmission = this.previousSubmissions.individual.find(
          (sub: any) => sub.filename === this.comparisonResult
        );

        if (selectedSubmission) {
          // Check if metrics exist on the selected submission
          if (selectedSubmission.metrics && selectedSubmission.metrics[analysisType]) {
            metrics = selectedSubmission.metrics[analysisType];
          }
        } else if (this.previousSubmissions.all) {
          metrics = this.previousSubmissions.all[analysisType];
        }
      }

      if (!metrics || !Array.isArray(metrics)) {
        return currentData[index]; // Return corresponding current data as fallback
      }

      // Sum the 'value' property from each metric object and fix to 4 decimal places
      const sum = metrics.reduce((acc: number, metric: any) => {
        const numValue = Number(metric.value);
        if (isNaN(numValue)) {
          return acc;
        }
        return acc + numValue;
      }, 0);

      return Number(sum.toFixed(4));
    }) as number[];

    const updatedOptions: Partial<ChartOptions> = {
      series: [
        {
          name: "Current submission",
          data: currentData
        },
        {
          name: this.comparisonResult === 'all' ?
            "AVG. Previous submissions" :
            "Selected submission",
          data: previousData
        }
      ],
      chart: {
        type: "radar" as const,
        height: 280,
        toolbar: {
          show: false
        },
        fontFamily: 'Inter, sans-serif',
        animations: {
          enabled: true
        }
      },
      plotOptions: {
        radar: {
          size: initialSize,
          offsetY: 0,
          polygons: {
            strokeWidth: '1',
            strokeColors: '#e8e8e8',
            connectorColors: '#e8e8e8',
            fill: {
              colors: ['#fcfcfc', '#ffffff']
            }
          }
        }
      },
      colors: ["#D42620", "#147AD6"],
      fill: {
        opacity: 0.15
      },
      stroke: {
        width: 2,
        colors: ["#D42620", "#147AD6"]
      },
      markers: {
        size: 4,
        hover: {
          size: 6
        }
      },
      xaxis: {
        labels: {
          trim: false,
          style: {
            colors: Array(4).fill("#666666"),
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
          },
          offsetY: 2,
          show: true
        }
      },
      yaxis: {
        show: false,
        min: 0,
        max: Math.max(...currentData, ...previousData) * 1.2, // Add 20% padding to max value
        tickAmount: 5,
        forceNiceScale: true
      },
      grid: {
        show: true,
        padding: {
          top: 20,
          right: 20,
          bottom: 40,
          left: 20
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 0,
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        markers: {
          width: 12,
          height: 12,
          radius: 2
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0
        }
      },
      labels: [
        "Lexical",
        "Syntactic",
        "Structural",
        "Readability"
      ]
    };

    // Force a complete chart update with new data
    if (this.chart && this.chart.updateSeries) {
      try {
        this.chart.updateSeries(updatedOptions.series, true);
      } catch (error) {
        // Silent failure
      }
    }

    // Use the safe update method with forced rendering and animation
    this.safeUpdateChart(updatedOptions, true, true);
  }

  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public onViewDetailReport(): void {
    // Implement view detail report logic
  }

  public toggleAnalysis(type: string): void {
    this.activeAnalysis = this.activeAnalysis === type ? null : type;
  }

  public onComparisonResultChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.comparisonResult = target.value;

    // If we're in stylometric view, return to classic view
    if (this.reportType === 'stylometric') {
      this.reportComponent.switchReportType('classic');
      this.reportType = 'classic';
      this.isStylometricReportOpen = false;
      this.selectedAnalysis = '';
      this.activeAnalysis = null;

      // Set analysis type to 'stylometrics' to ensure stylometrics section is unfolded in AI Bar
      this.reportService.selectAnalysisTypeSubject.next('stylometrics');

      // Force change detection to update the view
      this.cdr.detectChanges();

      // Use a timer to ensure the UI is updated before we try to find and click elements
      setTimeout(() => {
        // Try to find the stylometrics fold header in the AI Bar
        const foldHeaders = document.querySelectorAll('.fold-header');

        // Loop through all fold headers to find the stylometrics one
        foldHeaders.forEach(header => {
          // Find the h3 element inside this header
          const h3Element = header.querySelector('h3');

          // Check if this is the stylometrics fold header (contains "Stylometrics" text)
          if (h3Element && h3Element.textContent && h3Element.textContent.includes('Stylometrics')) {
            // If the header doesn't have the 'active' class (meaning it's folded), click it
            if (!header.classList.contains('active')) {
              (header as HTMLElement).click();
            }
          }
        });
      }, 300); // Increased timeout to ensure view has updated
    }

    // Safety check - if no value is selected, default to 'all'
    if (!this.comparisonResult) {
      this.comparisonResult = 'all';
    }

    if (this.projectSynapse) {
      const selectedModel = this.availableAIModels.find(model => model.value === this.comparisonResult);
      if (selectedModel) {
        // Comment out PDF URL setting for now as we're not using dual view
        // this.stylometricState.setSelectedModelPdfUrl(selectedModel.fileUrl);
        this.stylometricState.setSelectedAIModel(selectedModel);
      }
    } else {
      // Handle regular stylometric comparison
      const selectedSubmission = this.availableSubmissions.find(sub => sub.value === this.comparisonResult);
      if (selectedSubmission?.discrepancy) {
        this.currentSeverity = selectedSubmission.discrepancy.toLowerCase() as 'low' | 'medium' | 'high';
      }
    }

    // Notify the service about the selected submission change
    this.stylometricState.setSelectedSubmission(this.comparisonResult);

    // Update flag counts when comparison changes
    this.updateAnalysisFlagCounts();

    if (!this.projectSynapse) {
      // Use setTimeout to ensure this runs after the view is initialized
      setTimeout(() => {
        // Important: Force update of the chart
        this.initializeChartOptions();

        // Force change detection to update the UI
        this.cdr.detectChanges();

        // If the chart is already initialized, force a redraw
        if (this.chart && typeof this.chart.render === 'function') {
          try {
            this.chart.render();
          } catch (error) {
            // Silent failure
          }
        }
      }, 100); // Use a slightly longer delay to ensure all async operations complete
    }
  }

  // Method to handle toggling between report views while preserving analysis state
  toggleReportView(): void {
    if (this.reportType === 'stylometric' || this.isStylometricReportOpen) {
      // Currently in stylometric view, switch to classic view without losing analysis state
      this.reportComponent.switchReportType('classic');
      this.reportType = 'classic';
      this.isStylometricReportOpen = false;

      // Determine which section to unfold based on whether Project Synapse is active
      if (this.isProjectSynapseActive) {
        // Set analysis type to 'projectSynapse' to ensure Project Synapse section is unfolded in AI Bar
        this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
      } else {
        // Set analysis type to 'stylometrics' to ensure stylometrics section is unfolded in AI Bar
        this.reportService.selectAnalysisTypeSubject.next('stylometrics');
      }

      // Force change detection to update the view
      this.cdr.detectChanges();

      // Use a timer to ensure the UI is updated before we try to find and click elements
      setTimeout(() => {
        // Try to find the appropriate fold header in the AI Bar based on active section
        const foldHeaders = document.querySelectorAll('.fold-header');

        // Loop through all fold headers to find the right one
        foldHeaders.forEach(header => {
          // Find the h3 element inside this header
          const h3Element = header.querySelector('h3');

          if (h3Element && h3Element.textContent) {
            let shouldClick = false;

            // Check which fold header to open based on active state
            if (this.isProjectSynapseActive && h3Element.textContent.includes('AI Authorship (Advanced)')) {
              shouldClick = true;
            } else if (!this.isProjectSynapseActive && h3Element.textContent.includes('Stylometrics')) {
              shouldClick = true;
            }

            // If we found the right header and it's not already active, click it
            if (shouldClick && !header.classList.contains('active')) {
              (header as HTMLElement).click();
            }
          }
        });
      }, 300); // Increased timeout to ensure view has updated

      // Don't reset selectedAnalysis or activeAnalysis to preserve state
    } else {
      // Currently in classic view, switch to stylometric view
      // If we already have a selected analysis, use it; otherwise, use the first one
      const analysisToOpen = this.selectedAnalysis ?
        this.analyses.find(a => a.id === this.selectedAnalysis) || this.analyses[0] :
        this.analyses[0];

      this.stylometricState.setReportType('stylometric');
      this.stylometricState.setSelectedAnalysis(analysisToOpen.id);
      this.selectedAnalysis = analysisToOpen.id;
      this.activeAnalysis = analysisToOpen.type;
      this.reportType = 'stylometric';
      this.isStylometricReportOpen = true;
    }
  }

  openAnalysis(analysis?: any): void {
    if (!analysis) return;

    // Save the current Project Synapse state before making changes
    const wasProjectSynapseActive = this.isProjectSynapseActive;

    // Check if this analysis is already selected in stylometric view
    if (this.reportType === 'stylometric' && this.selectedAnalysis === analysis.id) {
      // Return to classic view without losing analysis state
      this.toggleReportView();
      return;
    }

    // Set up stylometric state
    this.stylometricState.setReportType('stylometric');
    this.stylometricState.setSelectedAnalysis(analysis.id);

    // Update component state
    this.selectedAnalysis = analysis.id;
    this.activeAnalysis = analysis.type;
    this.reportType = 'stylometric';
    this.isStylometricReportOpen = true;

    // Set analysis type to 'stylometrics' to ensure stylometrics section is unfolded in AI Bar
    this.reportService.selectAnalysisTypeSubject.next('stylometrics');
  }

  showSubmission(submission: string) {
    const selectedSubmission = this.previousSubmissions.individual.find(
      (sub: any) => sub.filename === submission
    );
    if (selectedSubmission?.fileUrl) {
      // Open in new tab with the PDF viewer route
      const url = this.router.createUrlTree(['/submission/pdf-viewer'], {
        queryParams: { fileUrl: selectedSubmission.fileUrl }
      }).toString();
      window.open(url, '_blank');
    }
  }

  // Add methods for threshold checking
  private exceedsThreshold(metricName: string, currentValue: number, previousValue: number): boolean {
    const threshold = this.metricsThresholds[this.formatMetricKey(metricName)];
    return Math.abs(currentValue - previousValue) > threshold;
  }

  // Helper method to format metric name as key
  private formatMetricKey(metricName: string): string {
    return metricName.toLowerCase().replace(/ /g, '_');
  }

  private getPreviousMetricValue(analysisType: string, metricName: string): number {
    if (this.isProjectSynapseActive) {
      if (!this.currentModelComparisonData || !this.currentModelComparisonData.comparison) {
        return 0;
      }

      // For Project Synapse, we need to extract the AI model's stylometric data from the comparison sections
      // The backend structure is: comparison.section_X.ai_generated.features.{analysisType}.{metricName}
      const sections = this.currentModelComparisonData.comparison;
      let totalValue = 0;
      let sectionCount = 0;

      // Normalize the metric name to match backend structure
      const normalizedMetricName = metricName.toLowerCase().replace(/ /g, '_');

      // Iterate through all sections to get the average AI model value
      for (const sectionKey in sections) {
        const section = sections[sectionKey];
        if (section && section.ai_generated && section.ai_generated.features && section.ai_generated.features[analysisType]) {
          const aiFeatures = section.ai_generated.features[analysisType];

          // Try different possible metric name formats
          let metricValue = null;

          // Direct match
          if (aiFeatures[normalizedMetricName] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName];
          }
          // Try with original casing
          else if (aiFeatures[metricName] !== undefined) {
            metricValue = aiFeatures[metricName];
          }
          // Try camelCase version
          else if (aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())];
          }
          // Try exact name matching from the stylometricMetrics to find the corresponding backend key
          else {
            // Look for partial matches in case of naming differences
            for (const [key, value] of Object.entries(aiFeatures)) {
              if (key.includes(normalizedMetricName) || normalizedMetricName.includes(key)) {
                metricValue = value as number;
                break;
              }
            }
          }

          if (metricValue !== null && !isNaN(Number(metricValue))) {
            totalValue += Number(metricValue);
            sectionCount++;
          }
        }
      }

      // Return the average value across all sections, or 0 if no valid data found
      if (sectionCount > 0) {
        const avgValue = totalValue / sectionCount;
        return avgValue;
      }

      // Fallback: try to get from document-level stylometrics if available
      if (this.currentModelComparisonData.document_stylometrics?.features?.[analysisType]?.[normalizedMetricName] !== undefined) {
        const docValue = this.currentModelComparisonData.document_stylometrics.features[analysisType][normalizedMetricName];
        return Number(docValue);
      }

      return 0;
    } else {
      // Regular stylometrics logic remains unchanged
      if (this.comparisonResult === 'all' && this.previousSubmissions?.all) {
        // Get value from averaged metrics
        const metricData = this.previousSubmissions.all[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.comparisonResult === 'none' && this.previousSubmissions?.all) {
        // If no specific comparison is selected, use 'all' as default
        const metricData = this.previousSubmissions.all[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.previousSubmissions?.individual) {
        // Get value from specific submission
        const submission = this.previousSubmissions.individual.find(
          (sub: any) => sub.filename === this.comparisonResult
        );
        const metricData = submission?.metrics[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      }
    }
    return 0;
  }

  private updateAnalysisFlagCounts(): void {
    if (!this.stylometricMetrics || !this.metricsThresholds) {
      return;
    }

    // For regular stylometrics, ensure we have previousSubmissions
    if (!this.isProjectSynapseActive && (!this.previousSubmissions || !this.previousSubmissions.all)) {
      return;
    }

    if (this.isProjectSynapseActive && !this.currentProjectSynapseData) {
      this.currentProjectSynapseData = {};

      // Try to recover data if possible
      if (this.projectSynapseData?.length > 0) {
        this.currentProjectSynapseData = this.projectSynapseData[0].comparisonData?.stylometrics?.features || {};
      }
    }

    // Track total flags for debugging
    let totalFlags = 0;


    this.analyses = this.analyses.map(analysis => {
      const analysisType = analysis.id.split('-')[0];
      const metrics = this.stylometricMetrics[analysisType] || [];

      // Track flags for this analysis type
      let flaggedCountForAnalysis = 0;


      // Process each metric
      metrics.forEach(metric => {
        const currentValue = Number(metric.value);
        const previousValue = this.getPreviousMetricValue(analysisType, metric.name);
        const thresholdKey = this.formatMetricKey(metric.name);
        const threshold = this.metricsThresholds[thresholdKey];

        // Only count as flagged if we have valid values and the difference exceeds/is less than threshold
        // For Project Synapse, we reverse the flag logic - similarity to AI is BAD (should be flagged)
        if (!isNaN(currentValue) && !isNaN(previousValue) && threshold !== undefined) {
          const diff = Math.abs(currentValue - previousValue);
          let isFlagged = false;

          if (this.isProjectSynapseActive) {
            // Project Synapse: flag if TOO SIMILAR (diff LESS THAN threshold)
            isFlagged = diff < threshold;
          } else {
            // Regular: flag if TOO DIFFERENT (diff GREATER THAN threshold)
            isFlagged = diff > threshold;
          }

          if (isFlagged) {
            flaggedCountForAnalysis++;
            totalFlags++;
          }
        }
      });

      return { ...analysis, flagCount: flaggedCountForAnalysis };
    });

    // Update Project Synapse stylistic discrepancy based on the selected model's data
    if (this.isProjectSynapseActive) {
      this.updateProjectSynapseDiscrepancy();
    }

    // Force change detection to ensure the UI updates
    this.cdr.detectChanges();
  }

  // Add new helper methods
  private initializeProjectSynapseData(activateProjectSynapse: boolean = false): void {
    if (!this.projectSynapseData || this.projectSynapseData.length === 0) {
      // Initialize with empty structure so we don't get undefined errors
      this.currentProjectSynapseData = {};

      // Reset available models
      this.availableAIModels = [];
      this.stylometricState.setAvailableAIModels([]);
      return;
    }

    try {
      // Only activate Project Synapse mode if explicitly requested
      if (activateProjectSynapse) {
        this.stylometricState.toggleProjectSynapseActive(true);
      }

      // Set local flag regardless of global state
      this.isProjectSynapseActive = this.stylometricState.getProjectSynapseActive();

      // Sort Project Synapse data by AI percentage in descending order
      const sortedProjectSynapseData = [...this.projectSynapseData].sort((a, b) => {
        const percentageA = a.comparisonData?.ai_percentage || 0;
        const percentageB = b.comparisonData?.ai_percentage || 0;
        return percentageB - percentageA; // Descending order (highest first)
      });

      // Process and normalize the Project Synapse data
      this.availableAIModels = sortedProjectSynapseData.map(model => {
        // Verify expected structure exists and repair if needed
        if (!model.comparisonData) {
          model.comparisonData = { stylometrics: { features: {} } };
        } else if (!model.comparisonData.stylometrics) {
          model.comparisonData.stylometrics = { features: {} };
        } else if (!model.comparisonData.stylometrics.features) {
          model.comparisonData.stylometrics.features = {};
        }

        return {
          value: model.modelName || 'Unknown Model',
          label: model.modelName || 'Unknown Model',
          title: model.modelName || 'Unknown Model',
          fileUrl: model.pdfUrl || '',  // We'll keep this for future dual-view implementation
          comparisonData: model.comparisonData
        };
      });

      // Share the available AI models with other components through the stylometricState service
      this.stylometricState.setAvailableAIModels(this.availableAIModels);

      // Initialize with the first model (which is now the one with highest percentage after sorting)
      const firstModel = this.availableAIModels[0];
      if (firstModel) {
        this.comparisonResult = firstModel.value;

        // Save the first model as the most probable model
        this.mostProbableModel = firstModel.value;

        // Set the model in the state service
        this.stylometricState.setSelectedAIModel(firstModel);

        // Store the data locally for direct access
        this.currentModelComparisonData = firstModel.comparisonData;

        // Extract stylistic discrepancy from the backend data
        let discrepancyFromBackend = null;

        // Check different possible locations for the stylistic_discrepancy
        if (firstModel.comparisonData.stylometrics?.stylistic_discrepancy) {
          discrepancyFromBackend = firstModel.comparisonData.stylometrics.stylistic_discrepancy;
        } else if (firstModel.comparisonData.document_stylometrics?.stylistic_discrepancy) {
          discrepancyFromBackend = firstModel.comparisonData.document_stylometrics.stylistic_discrepancy;
        } else {
          // Check in each section's stylometrics (take from first section that has it)
          const sections = firstModel.comparisonData.comparison || {};
          for (const sectionKey in sections) {
            const section = sections[sectionKey];
            if (section?.stylometrics?.stylistic_discrepancy) {
              discrepancyFromBackend = section.stylometrics.stylistic_discrepancy;
              break; // Use the first one found
            }
          }
        }

        // Set the discrepancy if found, otherwise default to 'low'
        if (discrepancyFromBackend) {
          this.projectSynapseStylisticDiscrepancy = discrepancyFromBackend.toLowerCase();
        } else {
          this.projectSynapseStylisticDiscrepancy = 'low';
        }

        // Force flag count update after data is loaded with a small delay to ensure everything is initialized
        setTimeout(() => {
          this.updateAnalysisFlagCounts();
          this.cdr.detectChanges();
        }, 100);
      } else {
        // Initialize with empty structure
        this.currentProjectSynapseData = {};
      }
    } catch (error) {
      // Initialize with empty structure on error
      this.currentProjectSynapseData = {};
    }
  }

  private initializeStylometricData(): void {
    // Add safety check for previousSubmissions
    if (!this.previousSubmissions || !this.previousSubmissions.individual || !Array.isArray(this.previousSubmissions.individual)) {
      console.warn('Cannot initialize stylometric data: previousSubmissions is not properly structured');
      this.availableSubmissions = [
        { value: 'all', label: 'All Submissions', title: 'All Submissions', fileUrl: '', discrepancy: this.averageStylisticDiscrepancy || 'low' }
      ];
      return;
    }

    this.availableSubmissions = [
      { value: 'all', label: 'All Submissions', title: 'All Submissions', fileUrl: '', discrepancy: this.averageStylisticDiscrepancy },
      ...this.previousSubmissions.individual.map((sub: any) => ({
        value: sub.filename,
        label: sub.filename,
        title: sub.title,
        fileUrl: sub.fileUrl,
        discrepancy: sub.discrepancy
      }))
    ];

    // Set initial comparison result if not already set
    if (!this.comparisonResult || this.comparisonResult === 'none') {
      this.comparisonResult = 'all';
      // Notify the service of the initial value
      this.stylometricState.setSelectedSubmission(this.comparisonResult);
    }

    // Set initial severity based on averageStylisticDiscrepancy
    if (this.averageStylisticDiscrepancy) {
      this.currentSeverity = this.averageStylisticDiscrepancy.toLowerCase() as 'low' | 'medium' | 'high';
    }

    // Update flag counts
    this.updateAnalysisFlagCounts();

    // Initialize chart options
    setTimeout(() => {
      this.initializeChartOptions();
    }, 100);

    // Force change detection
    this.cdr.detectChanges();
  }

  formatDate(date: any) {
    // Convert from '2025-02-26T10:28:49.286Z' to 'Feb 26, 2025'
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatAIModel(model) {
    const modelNames = {
      'gpt-3': 'OpenAI\'s GPT-3',
      'gpt-4': 'OpenAI\'s GPT-4',
      'gpt-4-turbo': 'OpenAI\'s GPT-4 Turbo',
      'gpt-4o': 'OpenAI\'s GPT-4o',
      'gpt-4o-mini': 'OpenAI\'s GPT-4o-mini',
      'gpt-o1-preview': 'OpenAI\'s O1 Preview',
      'gpt-3.5-turbo': 'OpenAI\'s GPT-3.5 Turbo',
      'gpt-3.5-turbo-16k': 'OpenAI\'s GPT-3.5 Turbo 16k',
      'claude-3-5-sonnet': 'Anthropic\'s Claude 3.5 Sonnet',
      'claude-3-opus': 'Anthropic\'s Claude 3 Opus',
      'claude-3-haiku': 'Anthropic\'s Claude 3 Haiku',
      'claude-3-7-sonnet': 'Anthropic\'s Claude 3.7 Sonnet',
      'claude-3-7-sonnet-thinking': 'Anthropic\'s Claude 3.7 Sonnet Reasoning Model',
      'deepseek-r1-distill-qwen-32b': 'Deepseek R1 Distill Qwen 32B',
      'gemma2-9b-it': 'Google\'s Gemma 2 9B-IT',
      'llama-3.3-70b': 'Meta\'s Llama 3.3 70B',
      'qwen-awq-32b': 'Qwen AWQ 32B',
      'qwen-qwq-32b': 'Qwen QWQ 32B',
    }

    return modelNames[model] || model;
  }

  // Don't forget to clean up the event listener
  ngOnDestroy() {
    window.removeEventListener('resize', () => this.updateChartSize());

    // Unsubscribe from all subscriptions to prevent memory leaks
    if (this.reportService.selectAnalysisTypeSubject) {
      const subscriptions = [
        this.stylometricState.isProjectSynapseActive$.subscribe(),
        this.stylometricState.isStylometricReportOpen$.subscribe(),
        this.stylometricState.selectedAnalysis$.subscribe(),
        this.stylometricState.selectedAIModel$.subscribe(),
        this.reportService.selectAnalysisTypeSubject.subscribe()
      ];

      subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    }
  }

  // Add a method to handle model changes
  onModelChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedModelValue = target.value;

    // If we're in stylometric view, return to classic view
    if (this.reportType === 'stylometric') {
      this.reportComponent.switchReportType('classic');
      this.reportType = 'classic';
      this.isStylometricReportOpen = false;
      this.selectedAnalysis = '';
      this.activeAnalysis = null;

      // Set analysis type to 'projectSynapse' to ensure Project Synapse section is unfolded in AI Bar
      this.reportService.selectAnalysisTypeSubject.next('projectSynapse');

      // Force change detection to update the view
      this.cdr.detectChanges();

      // Use a timer to ensure the UI is updated before we try to find and click elements
      setTimeout(() => {
        // Try to find the Project Synapse fold header in the AI Bar
        const foldHeaders = document.querySelectorAll('.fold-header');

        // Loop through all fold headers to find the Project Synapse one
        foldHeaders.forEach(header => {
          // Find the h3 element inside this header
          const h3Element = header.querySelector('h3');

          // Check if this is the Project Synapse fold header (contains "AI Authorship (Advanced)" text)
          if (h3Element && h3Element.textContent && h3Element.textContent.includes('AI Authorship (Advanced)')) {
            // If the header doesn't have the 'active' class (meaning it's folded), click it
            if (!header.classList.contains('active')) {
              (header as HTMLElement).click();
            }
          }
        });
      }, 300); // Increased timeout to ensure view has updated
    }

    // Find the selected model
    const selectedModel = this.availableAIModels.find(model => model.value === selectedModelValue);

    if (selectedModel) {
      // Update the current model
      this.currentAIModel = selectedModel.value;

      // Set the current model's full comparison data
      this.currentModelComparisonData = selectedModel.comparisonData;

      // Update stylometrics data
      if (selectedModel.comparisonData && selectedModel.comparisonData.stylometrics) {
        // Update features data - we'll extract this directly from comparison sections when needed
        // this.currentProjectSynapseData = selectedModel.comparisonData.stylometrics.features;

        // Extract stylistic discrepancy from the backend data
        let discrepancyFromBackend = null;

        // Check different possible locations for the stylistic_discrepancy
        if (selectedModel.comparisonData.stylometrics?.stylistic_discrepancy) {
          discrepancyFromBackend = selectedModel.comparisonData.stylometrics.stylistic_discrepancy;
        } else if (selectedModel.comparisonData.document_stylometrics?.stylistic_discrepancy) {
          discrepancyFromBackend = selectedModel.comparisonData.document_stylometrics.stylistic_discrepancy;
        } else {
          // Check in each section's stylometrics (take from first section that has it)
          const sections = selectedModel.comparisonData.comparison || {};
          for (const sectionKey in sections) {
            const section = sections[sectionKey];
            if (section?.stylometrics?.stylistic_discrepancy) {
              discrepancyFromBackend = section.stylometrics.stylistic_discrepancy;
              break; // Use the first one found
            }
          }
        }

        // Update stylistic discrepancy if available from backend
        if (discrepancyFromBackend) {
          this.projectSynapseStylisticDiscrepancy = discrepancyFromBackend.toLowerCase();
          // Emit discrepancy level change to parent component
          this.discrepancyLevelChange.emit(this.projectSynapseStylisticDiscrepancy);
        }
      }

      // Create an enhanced model object with a flag indicating source
      const enhancedModel = {
        ...selectedModel,
        _stylometricsUpdate: true, // Add flag to indicate source of update
        _timestamp: new Date().getTime() // Add timestamp to ensure the object is always new
      };

      // Update the model in the state service
      this.stylometricState.setSelectedAIModel(enhancedModel);

      // Also notify the reportService about the change to ensure document highlighting is updated
      if (selectedModel.comparisonData && selectedModel.comparisonData.comparison) {
        this.reportService.updateProjectSynapseComparison(selectedModel.comparisonData.comparison);
      }

      // Force update when model changes
      this.updateAnalysisFlagCounts();

      // Use setTimeout to ensure this runs after the view is initialized
      setTimeout(() => {
        this.initializeChartOptions();
      });

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  private updateProjectSynapseDiscrepancy(): void {
    if (!this.isProjectSynapseActive || !this.currentModelComparisonData) {
      return;
    }

    // First, try to get the discrepancy directly from the model's stylometrics data
    let discrepancyFromBackend = null;

    // Check different possible locations for the stylistic_discrepancy
    if (this.currentModelComparisonData.stylometrics?.stylistic_discrepancy) {
      discrepancyFromBackend = this.currentModelComparisonData.stylometrics.stylistic_discrepancy;
    } else if (this.currentModelComparisonData.document_stylometrics?.stylistic_discrepancy) {
      discrepancyFromBackend = this.currentModelComparisonData.document_stylometrics.stylistic_discrepancy;
    } else {
      // Check in each section's stylometrics
      const sections = this.currentModelComparisonData.comparison || {};
      for (const sectionKey in sections) {
        const section = sections[sectionKey];
        if (section?.stylometrics?.stylistic_discrepancy) {
          discrepancyFromBackend = section.stylometrics.stylistic_discrepancy;
          break; // Use the first one found
        }
      }
    }

    // If we found a discrepancy value from backend, use it
    if (discrepancyFromBackend) {
      const previousDiscrepancy = this.projectSynapseStylisticDiscrepancy;
      this.projectSynapseStylisticDiscrepancy = discrepancyFromBackend.toLowerCase();

      // Emit the value to parent component if it changed
      if (previousDiscrepancy !== this.projectSynapseStylisticDiscrepancy) {
        this.discrepancyLevelChange.emit(this.projectSynapseStylisticDiscrepancy);
      }
    } else {
      // Fallback: calculate based on flag counts
      let totalFlags = 0;

      const analysisTypes = ['lexical', 'syntactic', 'structural', 'readability'];
      analysisTypes.forEach(analysisType => {
        const analysisMetrics = this.stylometricMetrics[analysisType] || [];
        analysisMetrics.forEach(metric => {
          const currentValue = Number(metric.value);
          const previousValue = this.getPreviousMetricValue(analysisType, metric.name);
          const thresholdKey = this.formatMetricKey(metric.name);
          const threshold = this.metricsThresholds[thresholdKey];

          if (!isNaN(currentValue) && !isNaN(previousValue) && threshold !== undefined) {
            const diff = Math.abs(currentValue - previousValue);
            // For Project Synapse: flag if TOO SIMILAR (diff LESS THAN threshold)
            if (diff < threshold) {
              totalFlags++;
            }
          }
        });
      });

      const previousDiscrepancy = this.projectSynapseStylisticDiscrepancy;

      // Set discrepancy based on flag count
      if (totalFlags > 10) {
        this.projectSynapseStylisticDiscrepancy = 'low';
      } else if (totalFlags > 5) {
        this.projectSynapseStylisticDiscrepancy = 'medium';
      } else {
        this.projectSynapseStylisticDiscrepancy = 'high';
      }

      // Emit the value to parent component if it changed
      if (previousDiscrepancy !== this.projectSynapseStylisticDiscrepancy) {
        this.discrepancyLevelChange.emit(this.projectSynapseStylisticDiscrepancy);
      }
    }
  }
}
